import { BrandLine, Card, ContainerFluid, Grid, PageLayout, Pagination, Section, Text } from '@/atoms';
import { CardMedium } from '@/bloks/Card';
import { AccountantCityList } from '@/bloks/Page/NavigationPageFindAccountants/components/AccountantCityList/AccountantCityList';
import { CardFiveGroupStoryblok, NavigationPageFindAccountantsStoryblok, SubMenuStoryblok } from '@/components';
import { useAccountants } from '@/contexts/accountants/AccountantsProvider';
import { Accountant } from '@/contexts/accountants/types';
import { useSubMenu } from '@/contexts/menu/SubMenuProvider';
import { ThemeProvider } from '@/contexts/theme';
import { ISbStoryData } from '@/types/storyblok';
import { capitalizeEachWord, cls } from '@/utils';
import React, { useEffect, useState } from 'react';
import { t } from 'ttag';
import DynamicComponent from '../../DynamicComponent';
import styles from './NavigationPageFindAccountants.module.scss';
import { AccountantCard } from './components/AccountantCard/AccountantCard';
import { Filter, filterChoices } from './components/Filter/Filter';

interface Props {
	blok: NavigationPageFindAccountantsStoryblok;
	meta: Record<string, any>;
}

export const NavigationPageFindAccountants: React.FC<Props> = ({ blok, meta }) => {
	const contact = blok?.contact as unknown as ISbStoryData<CardFiveGroupStoryblok>;
	const subMenu = blok?.subMenu as unknown as ISbStoryData<SubMenuStoryblok>;
	const MAX_PER_PAGE = blok.customCard?.length ? 11 : 12;

	const {
		accountants,
		filteredAccountants,
		cities,
		searchAddress,
		currentPage,
		totalPages,
		setTotalPages,
		setCurrentPage,
		activeFilters,
	} = useAccountants();

	const [paginatedAccountants, setPaginatedAccountants] = useState<Accountant[] | null>(null);

	const { hasSubMenuV1 } = useSubMenu();

	useEffect(() => {
		const tempAccountants = activeFilters ? filteredAccountants : accountants;
		const tempTotalPages = Math.ceil(tempAccountants.length / MAX_PER_PAGE);
		let startIndex = 0;
		if (totalPages > 1) {
			startIndex = (currentPage - 1) * MAX_PER_PAGE;
		}
		const endIndex = MAX_PER_PAGE * currentPage;

		setTotalPages(tempTotalPages);
		setPaginatedAccountants(tempAccountants.slice(startIndex, endIndex));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accountants, filteredAccountants, currentPage]);

	const handlePaginationAndScroll = (event: any) => {
		const element = document.getElementById('accountants');
		if (element) {
			element.scrollIntoView({ behavior: 'smooth' });
		}
		handlePagination(event);
	};

	const handlePagination = (event: any) => {
		if (event.currentTarget.value === 'forward') {
			setCurrentPage(currentPage < totalPages ? currentPage + 1 : currentPage);
		} else if (event.currentTarget.value === 'back') {
			setCurrentPage(currentPage !== 1 ? currentPage - 1 : currentPage);
		}
	};

	const renderCustomCardGridItem = (blok: any) => {
		return (
			<Grid.Item key="special-card">
				<ThemeProvider theme="beige" key={blok._uid} applyChildTheme>
					<CardMedium titleSize="h3" key={blok._uid} {...CardMedium.blokProps({ blok, meta })} />
				</ThemeProvider>
			</Grid.Item>
		);
	};

	const renderAccountantCard = (index: number, accountant: Accountant) => {
		const isRenderSpecialCard = paginatedAccountants?.length === 1 || index === 1;

		return (
			<>
				<Grid.Item key={`accountant-${accountant.id}`} className={styles.cardContainer}>
					<AccountantCard accountant={accountant} filterChoices={filterChoices} />
				</Grid.Item>
				{isRenderSpecialCard && blok.customCard?.map((blok: any) => renderCustomCardGridItem(blok))}
			</>
		);
	};

	return (
		<PageLayout subMenu={subMenu} meta={meta} exits={contact}>
			{blok.header?.map((blok: any) => {
				if (searchAddress && accountants.length > 0) {
					const headerBlock = { ...blok };
					headerBlock.h1Part1 = `Redovisningsbyråer i ${capitalizeEachWord(searchAddress)}`;
					return <DynamicComponent key={headerBlock._uid} blok={headerBlock} />;
				} else {
					return <DynamicComponent key={blok._uid} blok={blok} />;
				}
			})}
			<ContainerFluid theme="white">
				<Section id="result" className={cls(hasSubMenuV1 && styles.hasSubMenuV1)} paddingY={{ base: '3' }}>
					<div className={styles.filterContainer}>
						<Filter maxPerPage={MAX_PER_PAGE} />
					</div>
					<div id="accountants" />
					{paginatedAccountants && paginatedAccountants.length > 0 ? (
						<div className={styles.accountantCards}>
							<div className={styles.paginationTop}>
								<Pagination onClick={handlePagination} currentPage={currentPage} totalPages={totalPages} />
							</div>
							<Grid columns={{ base: 1, md: 2, xl: 3, max: hasSubMenuV1 ? 3 : 4 }} colGap="2" rowGap="2">
								{paginatedAccountants?.map((accountant, index: number) => renderAccountantCard(index, accountant))}
							</Grid>
							<div className={styles.paginationBottom}>
								<Pagination onClick={handlePaginationAndScroll} currentPage={currentPage} totalPages={totalPages} />
							</div>
						</div>
					) : (
						<div className={styles.accountantCards}>
							<Grid columns={{ base: 1, md: 2, xl: 3, max: hasSubMenuV1 ? 3 : 4 }} colGap="2" rowGap="2">
								<Grid.Item colSpan={{ base: 1, max: 2 }}>
									<Card layout="center" border>
										<Text marginBottom="1-5" size="ingress" bold={true}>{t`Inga resultat`}</Text>
										<Text>{t`Kolla så du stavat rätt, testa att uttöka sökradie eller minska antalet filter`}</Text>
									</Card>
								</Grid.Item>
								{blok.customCard?.map((blok: any) => renderCustomCardGridItem(blok))}
							</Grid>
						</div>
					)}
					<div className={styles.footer}>
						<Grid
							className={styles.gridContainer}
							columns={{ base: 1, lg: blok?.footer?.length || 0 }}
							colGap="1"
							rowGap="2"
						>
							{blok.footer?.map((blok) => (
								<div key={blok._uid}>
									<BrandLine className={styles.brandLine} />
									<DynamicComponent blok={blok} />
								</div>
							))}
						</Grid>
					</div>
				</Section>
				<AccountantCityList cities={cities} />
			</ContainerFluid>
		</PageLayout>
	);
};
